import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { ClipboardList, Download, Users, Clock, Calendar, Link2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { pesquisaAPI } from '../../lib/api';
import { brl } from '../../components/pesquisa/PesquisaUI';
import { AdminPesquisaTable } from '../../components/pesquisa/AdminPesquisaTable';
import { EditarRespostaDialog } from '../../components/pesquisa/EditarRespostaDialog';

const StatCard = ({ icon: Icon, color, value, label, testId }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid={testId}>
    <div className="flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}><Icon className="w-6 h-6" /></div>
      <div>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        <p className="text-sm text-slate-500">{label}</p>
      </div>
    </div>
  </div>
);

const baixarBlob = (blob, nome) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  a.click();
  window.URL.revokeObjectURL(url);
};

const AdminPesquisaPlantao = () => {
  const [dados, setDados] = useState({ total: 0, resumo: null, respostas: [] });
  const [loading, setLoading] = useState(true);
  const [exportando, setExportando] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [editando, setEditando] = useState(null);

  const carregar = async () => {
    try {
      const r = await pesquisaAPI.listar();
      setDados(r.data);
    } catch {
      toast.error('Erro ao carregar respostas da pesquisa');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregar(); }, []);

  const exportar = async (arquivo) => {
    setExportando(arquivo);
    try {
      const r = await pesquisaAPI.exportar(arquivo);
      const match = /filename="?([^"]+)"?/.exec(r.headers['content-disposition'] || '');
      baixarBlob(r.data, match?.[1] || `${arquivo.replace('.csv', '')}-${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success('Arquivo exportado');
    } catch {
      toast.error('Erro ao exportar');
    } finally {
      setExportando('');
    }
  };

  const excluir = async () => {
    if (!deleteId) return;
    try {
      await pesquisaAPI.excluir(deleteId);
      toast.success('Resposta excluída');
      carregar();
    } catch {
      toast.error('Erro ao excluir resposta');
    } finally {
      setDeleteId(null);
    }
  };

  const copiarLink = async () => {
    const link = `${window.location.origin}/plantao.html`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Link da pesquisa copiado');
    } catch {
      window.prompt('Copie o link da pesquisa:', link);
    }
  };

  const { respostas, resumo } = dados;
  const comContato = respostas.filter((r) => r.contato || r.nome).length;
  const now = new Date();
  const esteMes = respostas.filter((r) => {
    const d = new Date(r.criado_em);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;

  return (
    <div data-testid="admin-pesquisa-plantao">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Pesquisa de Plantão</h1>
          <p className="text-slate-600">Respostas anônimas sobre valores de plantão. Textos da página editáveis em "Conteúdo do Site".</p>
          <p className="mt-2 text-sm text-slate-500">
            Link para compartilhar (com cartão de prévia no WhatsApp):{' '}
            <a href="/plantao.html" target="_blank" rel="noopener noreferrer" className="font-mono text-primary hover:underline" data-testid="pesquisa-link-compartilhar">
              {window.location.host}/plantao.html
            </a>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={copiarLink} data-testid="pesquisa-copiar-link-button">
            <Link2 className="w-4 h-4 mr-2" />Copiar link
          </Button>
          <Button variant="outline" onClick={() => exportar('leads.csv')} disabled={comContato === 0 || !!exportando} data-testid="pesquisa-export-leads-button">
            {exportando === 'leads.csv' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
            Exportar contatos
          </Button>
          <Button onClick={() => exportar('export.csv')} disabled={respostas.length === 0 || !!exportando} className="btn-primary" data-testid="pesquisa-export-csv-button">
            {exportando === 'export.csv' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Exportar CSV completo
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <StatCard icon={ClipboardList} color="bg-blue-100 text-blue-600" value={dados.total} label="Respostas" testId="stat-total" />
        <StatCard icon={Calendar} color="bg-green-100 text-green-600" value={esteMes} label="Este mês" testId="stat-mes" />
        <StatCard icon={Clock} color="bg-emerald-100 text-emerald-600" value={resumo?.geral?.mediana_hora ? brl(resumo.geral.mediana_hora) : '—'} label="Mediana por hora" testId="stat-mediana-hora" />
        <StatCard icon={Users} color="bg-amber-100 text-amber-600" value={comContato} label="Deixaram contato" testId="stat-contatos" />
      </div>

      <AdminPesquisaTable respostas={respostas} loading={loading} onDelete={setDeleteId} onEdit={setEditando} />

      <EditarRespostaDialog resposta={editando} onClose={() => setEditando(null)} onSaved={carregar} />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir resposta?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita. A resposta será removida da pesquisa.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={excluir} className="bg-red-600 hover:bg-red-700" data-testid="pesquisa-confirm-delete">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPesquisaPlantao;
